/**
 * CSC native localStorage wishlist (E6).
 *
 * No third-party app. State lives in localStorage under key `csc-wishlist`
 * as a JSON array of items:
 *   { id, handle, title, image, price, addedAt }
 *
 * The heart button in snippets/wishlist-heart.liquid calls
 * `window.cscWishlist.toggle(button)` on click. On DOMContentLoaded
 * this script hydrates every heart on the page (collection cards,
 * PDP main image, wishlist page) by adding `.csc-wishlist-heart--filled`
 * to any heart whose data-product-id is in the saved list.
 *
 * The wishlist page (templates/page.wishlist.liquid) renders into
 * `#csc-wishlist-grid` and listens for `wishlist:changed` to re-render.
 *
 * Storage cap: 50 items. LRU eviction by addedAt (oldest first).
 */
(function () {
  if (window.cscWishlist) return; // guard against double-load

  var STORAGE_KEY = 'csc-wishlist';
  var MAX_ITEMS = 50;
  var FILLED_CLASS = 'csc-wishlist-heart--filled';

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      // LRU evict if over cap (sort by addedAt asc, drop oldest)
      if (list.length > MAX_ITEMS) {
        list.sort(function (a, b) { return (a.addedAt || 0) - (b.addedAt || 0); });
        list = list.slice(-MAX_ITEMS);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      // Quota exceeded or disabled — silently fail. The heart will
      // still toggle visually for the current pageview but won't persist.
    }
  }

  function emitChange(action, item) {
    document.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { action: action, item: item, count: read().length }
    }));
  }

  function findIndex(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(id)) return i;
    }
    return -1;
  }

  function hydrate(scope) {
    var root = scope || document;
    var list = read();
    var idSet = {};
    list.forEach(function (it) { idSet[String(it.id)] = true; });
    var hearts = root.querySelectorAll('.csc-wishlist-heart[data-product-id]');
    for (var i = 0; i < hearts.length; i++) {
      var btn = hearts[i];
      var id = btn.getAttribute('data-product-id');
      var filled = !!idSet[String(id)];
      btn.classList.toggle(FILLED_CLASS, filled);
      btn.setAttribute('aria-pressed', filled ? 'true' : 'false');
      var title = btn.getAttribute('data-product-title') || 'product';
      btn.setAttribute('aria-label', (filled ? 'Remove ' : 'Add ') + title + (filled ? ' from wishlist' : ' to wishlist'));
    }
  }

  function toggle(btn) {
    if (!btn || !btn.getAttribute) return;
    var id = btn.getAttribute('data-product-id');
    if (!id) return;

    var list = read();
    var idx = findIndex(list, id);
    var action;
    var item;

    if (idx >= 0) {
      item = list[idx];
      list.splice(idx, 1);
      action = 'remove';
    } else {
      item = {
        id: id,
        handle: btn.getAttribute('data-product-handle') || '',
        title: btn.getAttribute('data-product-title') || '',
        image: btn.getAttribute('data-product-image') || '',
        price: btn.getAttribute('data-product-price') || '',
        addedAt: Date.now()
      };
      list.push(item);
      action = 'add';
    }

    write(list);
    // Re-hydrate every matching heart on the page (collection cards may
    // have multiple hearts for the same product across no-media + standard
    // render paths).
    hydrate();
    emitChange(action, item);
  }

  function get() { return read(); }

  function clearAll() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    hydrate();
    emitChange('clear', null);
  }

  // ── Wishlist page renderer ──────────────────────────────────────────
  // If a #csc-wishlist-grid mount point exists (templates/page.wishlist),
  // render the saved items into it and re-render on every change.

  function moneyFromCents(cents) {
    if (cents === '' || cents === null || cents === undefined) return '';
    var n = parseInt(cents, 10);
    if (isNaN(n)) return '';
    // Shopify GBP — divide by 100, format with 2 decimals OR no decimals
    // if .00. Currency symbol is hardcoded £ for the UK storefront.
    var pounds = n / 100;
    var formatted = pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2);
    return '£' + formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function renderGrid() {
    var grid = document.getElementById('csc-wishlist-grid');
    if (!grid) return;
    var list = read();
    var emptyEl = document.getElementById('csc-wishlist-empty');

    if (list.length === 0) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;

    // Sort by most-recently-added first
    list.sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });

    var html = list.map(function (it) {
      var url = '/products/' + (it.handle || '');
      var price = moneyFromCents(it.price);
      var img = it.image ? '<img src="' + escapeAttr(it.image) + '" alt="" loading="lazy" decoding="async">' : '';
      return [
        '<article class="csc-wishlist-item" data-product-id="', escapeAttr(it.id), '">',
          '<a class="csc-wishlist-item__link" href="', escapeAttr(url), '">',
            '<div class="csc-wishlist-item__media">', img, '</div>',
            '<div class="csc-wishlist-item__meta">',
              '<h3 class="csc-wishlist-item__title">', escapeHtml(it.title), '</h3>',
              price ? '<p class="csc-wishlist-item__price">' + price + '</p>' : '',
            '</div>',
          '</a>',
          '<button type="button" class="csc-wishlist-item__remove" data-remove-id="', escapeAttr(it.id), '" aria-label="Remove ', escapeAttr(it.title), ' from wishlist">Remove</button>',
        '</article>'
      ].join('');
    }).join('');
    grid.innerHTML = html;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // Delegated click handler for wishlist page Remove buttons
  function bindWishlistPageRemovals() {
    var grid = document.getElementById('csc-wishlist-grid');
    if (!grid) return;
    grid.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-remove-id]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-remove-id');
      var list = read();
      var idx = findIndex(list, id);
      if (idx < 0) return;
      var item = list[idx];
      list.splice(idx, 1);
      write(list);
      hydrate();
      emitChange('remove', item);
    });
  }

  // ── Init ────────────────────────────────────────────────────────────
  function init() {
    hydrate();
    renderGrid();
    bindWishlistPageRemovals();
    document.addEventListener('wishlist:changed', renderGrid);
    // Cross-tab sync: a different tab toggling the wishlist updates this one.
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY) {
        hydrate();
        renderGrid();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.cscWishlist = {
    toggle: toggle,
    get: get,
    clear: clearAll,
    hydrate: hydrate
  };
})();
